import CloseIcon from "@mui/icons-material/Close";
import { AppBar, Dialog, IconButton, Stack, Toolbar, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { CSSProperties, useEffect, useState } from "react";
import { ImageDialogContent, OpenHandler } from "./styles";
import PDFViewer from "../PDFViewer";
import { TDocumentFileInfo } from "../../models";
import {
  FileType,
  getFileTypeFromMimeType,
} from "../../utils/fileTypeDetection";

export type Props = {
  requestId: string;
  documentId: string;
  fileId: string;
  dataLink: string;
  fileInfo?: TDocumentFileInfo | null;
};

export const FileContent = (props: Props) => {
  const { dataLink, fileInfo } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [currentFileType, setCurrentFileType] = useState<FileType>();

  useEffect(() => {
    let isCancelled = false;

    const metadataFileType = getFileTypeFromMimeType(
      fileInfo?.mimeType,
    );
    if (metadataFileType) {
      setCurrentFileType(metadataFileType);
      return;
    }

    setCurrentFileType(undefined);

    const getContentType = async () => {
      if (dataLink) {
        try {
          const response = await fetch(dataLink);
          const contentType = response.headers.get("content-type");
          if (!isCancelled) {
            setCurrentFileType(
              getFileTypeFromMimeType(contentType) ??
                FileType.other,
            );
          }
        } catch {
          // The type probe is a best-effort fallback for legacy file rows.
          if (!isCancelled) {
            setCurrentFileType(FileType.other);
          }
        }
      }
    };

    getContentType();

    return () => {
      isCancelled = true;
    };
  }, [dataLink, fileInfo?.mimeType]);

  const thumbnailStyles: CSSProperties = {
    width: 280,
    height: 280,
    maxWidth: 280,
    maxHeight: 280,
    borderRadius: 2,
    padding: 1,
    overflow: "hidden",
    position: "relative",
    backgroundColor: "#f0f0f0",
    backgroundPosition: "center center",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
  };

  if (dataLink && currentFileType === FileType.image) {
    thumbnailStyles.backgroundImage = `url(${dataLink})`;
  }

  const isPreviewable =
    currentFileType === FileType.image ||
    currentFileType === FileType.pdf ||
    currentFileType === FileType.video;
  const fileName = fileInfo?.originalName || props.fileId;

  const handleOpen = () => {
    if (!dataLink) return;
    if (!isPreviewable) {
      window.open(dataLink, "_blank", "noopener,noreferrer");
      return;
    }
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const allowOpen = Boolean(dataLink);

  const renderThumbnail = () => {
    if (currentFileType === FileType.pdf) {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            color: "text.secondary",
          }}
        >
          PDF
        </Box>
      );
    }
    if (currentFileType === FileType.video) {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            color: "text.secondary",
          }}
        >
          Video
        </Box>
      );
    }
    if (currentFileType === FileType.doc || currentFileType === FileType.other) {
      return (
        <Stack
          sx={{
            width: "100%",
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            textAlign: "center",
            gap: 0.75,
            px: 2,
          }}
        >
          <Typography variant="body2" color="text.primary">
            {currentFileType === FileType.doc ? "Document" : "File"}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              maxWidth: "100%",
              overflowWrap: "anywhere",
            }}
          >
            {fileName}
          </Typography>
          {allowOpen && (
            <Typography variant="caption" color="primary">
              Open file
            </Typography>
          )}
        </Stack>
      );
    }
    return null;
  };

  const renderFullscreenContent = () => {
    if (currentFileType === FileType.pdf) {
      return <PDFViewer path={dataLink} />;
    }
    if (currentFileType === FileType.video) {
      return (
        <video
          src={dataLink}
          controls
          style={{ maxWidth: "100%", maxHeight: "100%" }}
        />
      );
    }
    return (
      <img
        src={dataLink}
        alt=''
        style={{ maxWidth: "100%", maxHeight: "100%" }}
      />
    );
  };

  return (
    <Box sx={thumbnailStyles} data-file-type={currentFileType ?? ""}>
      {currentFileType === FileType.image
        ? null
        : renderThumbnail()}
      {allowOpen && <OpenHandler onClick={handleOpen} />}
      {isOpen && (
        <Dialog fullScreen open={isOpen} onClose={handleClose}>
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <IconButton
                edge='start'
                color='inherit'
                onClick={handleClose}
                aria-label='close'
              >
                <CloseIcon />
              </IconButton>
              <Typography sx={{ ml: 2, flex: 1 }} variant='h6' component='div'>
                File View
              </Typography>
            </Toolbar>
          </AppBar>
          <ImageDialogContent>{renderFullscreenContent()}</ImageDialogContent>
        </Dialog>
      )}
    </Box>
  );
};
